import {NumberHelper} from "~/helpers/numberHelper"
import ResizeObserver from 'resize-observer-polyfill'

/**
 * A custom scrollbar for horizontal scrolling of an element inside the page
 * Sticks to the bottom of the viewport while bottom of scrollable element is below viewport
 * Sticks to the bottom of th element while the bottom of the scrollable element is in or above the viewport
 */
export default class StickyScrollBar {
  /**
   * element that has the scrollbar
   * @private
   */
  private scrollArea: HTMLElement
  /**
   * element inside the scrollable area that has the overflowing content
   * @private
   */
  private contentArea: HTMLElement
  /**
   * scrollbar inner element representing the current scroll position
   * @private
   */
  private thumb: HTMLElement
  /**
   * scrollbar outer element representing the area where the "thumb" can slide
   * @private
   */
  private track: HTMLElement
  private dragging: boolean = false
  private dragX: number = 0
  private observer: ResizeObserver

  constructor(private scrollAreaId: string, private overflowingContentId: string) {
    this.resize = this.resize.bind(this)
    this.resolveScrollClick = this.resolveScrollClick.bind(this)
    this.stopDrag = this.stopDrag.bind(this)
    this.drag = this.drag.bind(this)
    this.updatePosition = this.updatePosition.bind(this)
  }

  get scale(): number {
    return this.scrollArea.clientWidth / this.contentArea.clientWidth
  }

  get scrollPosition(): number {
    return this.scrollArea.scrollLeft
  }

  get thumbStartX(): number {
    return this.scrollPosition * this.scale
  }

  init(): void {
    this.scrollArea = document.getElementById(this.scrollAreaId)
    this.contentArea = document.getElementById(this.overflowingContentId)
    this.buildScrollBar()
    this.bindEventListeners()
  }

  destroy(): void {
    this.removeEventListeners()
    this.destroyScrollBar()
  }

  private updateSize() {
    this.track.style.width = `${this.scrollArea.clientWidth}px`
    this.thumb.style.width = `${this.scrollArea.clientWidth * this.scale}px`
  }

  private updatePosition() {
    this.thumb.style.marginLeft = `${this.scrollPosition * this.scale}px`
  }

  private updateVisibility() {
    this.track.classList.toggle('d-none', this.contentArea.clientWidth <= this.scrollArea.clientWidth)
  }

  private resize() {
    this.updateSize()
    this.updatePosition()
    this.updateVisibility()
  }

  private buildScrollBar(): any {
    this.track = document.createElement("div")
    this.track.classList.add('sticky-scroll', 'scrollbar-track')

    this.thumb = document.createElement("div")
    this.thumb.classList.add('sticky-scroll', 'scrollbar-thumb')
    this.track.appendChild(this.thumb)

    this.resize()
    this.scrollArea.after(this.track)
  }

  private resolveScrollClick(e: MouseEvent): any {
    if (e.button !== 0) return // not left click
    pauseEvent(e)

    if (e.target === this.track) {
      this.jump(e)
    } else if (e.target === this.thumb) {
      this.startDrag(e)
    }
  }

  private jump(e: MouseEvent): any {
    let amount = this.scrollArea.clientWidth
    if (e.offsetX < this.thumbStartX) amount *= -1
    const newPosition = this.scrollPosition + amount
    this.scroll(newPosition, e)
  }

  /**
   * start drag (i.e. mousedown)
   */
  private startDrag(e: MouseEvent) {
    this.dragging = true
    this.dragX = e.x
    this.scrollArea.style.scrollBehavior = 'initial'
  }

  /**
   * stopped drag (i.e. mouseup)
   */
  private stopDrag(): any {
    this.dragging = false
    this.scrollArea.style.scrollBehavior = null
  }

  /**
   * currently dragging (i.e. mousemove)
   */
  private drag(e: MouseEvent): any {
    if (this.dragging) {
      pauseEvent(e)
      const amount = (e.x - this.dragX) / this.scale
      this.scroll(this.scrollArea.scrollLeft + amount, e)
    }
  }

  private scroll(scrollPoint: number, e: MouseEvent) {
    this.scrollArea.scrollLeft = NumberHelper.clamp(scrollPoint, 0, this.contentArea.clientWidth)
    this.updatePosition()
    this.dragX = NumberHelper.clamp(e.x, 0, this.track.clientWidth)
  }

  private destroyScrollBar(): void {
    this.track.remove()
  }

  private bindEventListeners(): void {
    this.observer = new ResizeObserver(this.resize)
    this.observer.observe(this.scrollArea)
    this.track.addEventListener('mousedown', this.resolveScrollClick)
    window.addEventListener('mouseup', this.stopDrag)
    window.addEventListener('mousemove', this.drag)
    this.scrollArea.addEventListener('scroll', this.updatePosition)
  }

  private removeEventListeners(): void {
    this.observer.unobserve(this.scrollArea)
    this.track.removeEventListener('mousedown', this.resolveScrollClick)
    window.removeEventListener('mouseup', this.stopDrag)
    window.removeEventListener('mousemove', this.drag)
    this.scrollArea.removeEventListener('scroll', this.updatePosition)
  }
}

function pauseEvent(e: Event) {
  if (e.stopPropagation) e.stopPropagation()
  if (e.preventDefault) e.preventDefault()
  e.cancelBubble = true
  e.returnValue = false
  return false
}
